import React, { useState, useEffect, useRef } from 'react';
import { CompanyJobfairInfo, stallMap } from '../stallMapData_2026';
import { useLocation, useNavigate } from 'react-router-dom';
import './InteractivePlan.css';
import ButtonForm from './ButtonForm';

const ikarieraCompanyLinksCsv = require('../ikariera_company_links_2026.csv');

interface PlanProps {
  svgPath: string;
  svgPathMobile: string;
}

const STALL_STYLE_HIGHLIGHTED = "fill: #027cdb;fill-opacity:0.5;stroke:none;stroke-width:0.782855;stroke-opacity:0.966825";
const STALL_STYLE_IDLE = "fill:white;fill-opacity:0;stroke:none;stroke-width:0;stroke-opacity:0";
const STALL_STYLE_ACTIVE = "fill: #39c702;fill-opacity:0.9;stroke:none;stroke-width:0.782855;stroke-opacity:0.966825";
const isIndirectParticipation = (stallNumber: number | null) => stallNumber !== null && stallNumber >= 47; // Set for indirect participation, adjust every year !!!
const normalizeSearchValue = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const normalizeCompanyKey = (value: string) => normalizeSearchValue(value.trim());
const sortCompaniesAlphabetically = (companies: Iterable<CompanyJobfairInfo>) =>
  Array.from(companies).sort((a, b) => a.companyName.localeCompare(b.companyName, 'cs', { sensitivity: 'base' }));

const parseIkarieraLinksCsv = (csvText: string) => {
  const links = new Map<string, string>();

  csvText
    .split(/\r?\n/)
    .slice(1)
    .forEach((row) => {
      const trimmedRow = row.trim();

      if (!trimmedRow) {
        return;
      }

      const match = trimmedRow.match(/^(?:"([^"]*)"|([^,]*)),(.*)$/);

      if (!match) {
        return;
      }

      const companyName = (match[1] ?? match[2] ?? '').trim();
      const ikarieraLink = match[3]?.trim();

      if (!companyName || !ikarieraLink) {
        return;
      }

      links.set(normalizeCompanyKey(companyName), ikarieraLink);
    });

  return links;
};


const MOBILE_BREAKPOINT = 1300;
const onMobile = window.innerWidth <= MOBILE_BREAKPOINT;

function getCompanies(toggledButtonsMajorA: Set<string>, toggledButtonsMajorB: Set<string>): Set<CompanyJobfairInfo> {
  var list = new Set<CompanyJobfairInfo>();
  const hasAllOborySelected = onMobile
    ? toggledButtonsMajorA.has("Všechny obory") || toggledButtonsMajorA.size === 0
    : toggledButtonsMajorA.size === 0;
  const hasAllUvazekSelected = onMobile
    ? toggledButtonsMajorB.has("Všechny úvazky") || toggledButtonsMajorB.size === 0
    : false;

  // Iterate through each company
  for (var company of stallMap.values()) {
    let matchesObor = hasAllOborySelected;
    let matchesUvazek = hasAllUvazekSelected;

    // Check if the company matches any selected "obor"
    if (toggledButtonsMajorA.has("Strojní") && company.s_strojni) matchesObor = true;
    if (toggledButtonsMajorA.has("Elektro") && company.s_elektro) matchesObor = true;
    if (toggledButtonsMajorA.has("Informatika") && company.s_it) matchesObor = true;
    if (toggledButtonsMajorA.has("Ekonomie") && company.s_ekonomie) matchesObor = true;
    if (toggledButtonsMajorA.has("Chemie") && company.s_chemie) matchesObor = true;
    if (toggledButtonsMajorA.has("Stavebnictví") && company.s_stavebnictvi) matchesObor = true;
    if (toggledButtonsMajorA.has("Ostatní") && company.s_ostatni) matchesObor = true;

    // Check if the company matches any selected "uvazek"
    if (toggledButtonsMajorB.has("Plný úvazek")) matchesUvazek = true;
    if (toggledButtonsMajorB.has("Částečný úvazek") && company.m_cast_uvazek) matchesUvazek = true;
    if (toggledButtonsMajorB.has("Diplomka") && company.m_diplomka) matchesUvazek = true;
    if (toggledButtonsMajorB.has("Stáž") && (company.m_internship || company.m_trainee)) matchesUvazek = true;
    if (toggledButtonsMajorB.has("Brigáda") && company.m_brigada) matchesUvazek = true;

    // If the company matches both "obor" and "uvazek", add it to the list
    if (matchesObor && matchesUvazek) {
      list.add(company);
    }
  }

  return list;
}

const InteractivePlan: React.FC<PlanProps> = (props) => {
  const [toggledButtonsMajorA, setToggledButtonsMajorA] = useState(new Set<string>(onMobile ? ["Všechny obory"] : []));
  const [toggledButtonsMajorB, setToggledButtonsMajorB] = useState(
    new Set(onMobile ? ["Všechny úvazky"] : ["Plný úvazek", "Částečný úvazek", "Diplomka", "Stáž", "Brigáda"])
  );
  //const [toggledButtonsMajorB, setToggledButtonsMajorB] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchWasRecent, setSearchWasRecent] = useState<boolean>(true);
  const [ikarieraLinks, setIkarieraLinks] = useState<Map<string, string>>(new Map());
  const handledUrlSelection = useRef(false);
  const linkedStallNumber = useRef<number | null>(null);
  const userStartedBrowsing = useRef(false);

  const STALL_NUM = 68;

  const [active, setActive] = useState(0);

  var planId: string;

  if (onMobile) {
    planId = "jobfair-plan-mobile";
  }
  else {
    planId = "jobfair-plan";
  }

  // Hook to access location object
  const location = useLocation();
  const navigate = useNavigate();

  const setToggledButtonsMajorAWithBrowseTracking: React.Dispatch<React.SetStateAction<Set<string>>> = (value) => {
    userStartedBrowsing.current = true;
    linkedStallNumber.current = null;
    setActive(0);
    setToggledButtonsMajorA(value);
  };

  const setToggledButtonsMajorBWithBrowseTracking: React.Dispatch<React.SetStateAction<Set<string>>> = (value) => {
    userStartedBrowsing.current = true;
    linkedStallNumber.current = null;
    setActive(0);
    setToggledButtonsMajorB(value);
  };

  useEffect(() => {
    let isActive = true;

    fetch(ikarieraCompanyLinksCsv)
      .then((response) => response.text())
      .then((csvText) => {
        if (isActive) {
          setIkarieraLinks(parseIkarieraLinksCsv(csvText));
        }
      })
      .catch(() => {
        if (isActive) {
          setIkarieraLinks(new Map());
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    var jobfairPlan: any = document.getElementById(planId);

    // Highlight stalls based on toggledButtonsMajorA
    const highlightStallsBlue = () => {
      if (jobfairPlan?.contentDocument) {
        if ((onMobile && toggledButtonsMajorA.has("Všechny obory")) || (!onMobile && toggledButtonsMajorA.size === 0)) {
          return;
        }

        var svgDoc = jobfairPlan.contentDocument;
        const filteredCompanies = getCompanies(toggledButtonsMajorA, toggledButtonsMajorB);
        filteredCompanies.forEach((company) => {
          const stall = svgDoc.getElementById("rect-stall-" + company.stallNumber);
          if (stall) {
            stall.setAttribute("style", STALL_STYLE_HIGHLIGHTED);
          }
        });
      }
    };

    const highlightStalls = () => {
      if (jobfairPlan?.contentDocument) {
        var svgDoc = jobfairPlan.contentDocument;
        // Reset all stalls to idle style first, if necessary
        for (let i = 1; i <= STALL_NUM; i++) {
          const stallNumber = i;
          const stall = svgDoc.getElementById("rect-stall-" + stallNumber);
          stall?.setAttribute("style", STALL_STYLE_IDLE);

          stall?.addEventListener("click", function (event: any) {
            // Reset all stalls to idle
            for (let i = 1; i <= STALL_NUM; i++) {
              const stall = svgDoc.getElementById("rect-stall-" + i);
              stall?.setAttribute("style", STALL_STYLE_IDLE);
            }
            highlightStallsBlue();
            userStartedBrowsing.current = true;
            linkedStallNumber.current = null;
            setActive(stallNumber);
            // Highlight the clicked stall
            event.currentTarget.setAttribute("style", STALL_STYLE_ACTIVE);
            showActiveCompany(stallNumber);
          }, false);

          // Change cursor on hover
          stall?.addEventListener("mouseenter", function (event: any) {
            event.currentTarget.style.cursor = 'pointer';
          }, false);
        }

        const dpRect = svgDoc.getElementById("rect-dp");
        if (dpRect) {
          dpRect.onclick = () => navigate("/timelinePage");
          dpRect.onmouseenter = (event: any) => {
            event.currentTarget.style.cursor = 'pointer';
          };
        }

        const workshopRect = svgDoc.getElementById("rect-workshop");
        if (workshopRect) {
          workshopRect.onclick = () => navigate("/timelinePage#workshops");
          workshopRect.onmouseenter = (event: any) => {
            event.currentTarget.style.cursor = 'pointer';
          };
        }

        // firstly set blue highlight
        highlightStallsBlue();

        // secondly set active stall, red highlight
        if (active) {
          const activeStall_div = svgDoc.getElementById("rect-stall-" + active);
          activeStall_div?.setAttribute("style", STALL_STYLE_ACTIVE);
        }
      }
    };

    const highlightStallMobile = (stallNumber_: number | null) => {
      if (!jobfairPlan?.contentDocument) {
        return;
      }

      var svgDoc = jobfairPlan.contentDocument;
      for (let i = 1; i <= STALL_NUM; i++) {
        const stall = svgDoc.getElementById("rect-stall-" + i);
        stall?.setAttribute("style", STALL_STYLE_IDLE);
      }
      highlightStallsBlue();

      if (stallNumber_ !== null) {
        const stall = svgDoc.getElementById("rect-stall-" + stallNumber_);
        stall?.setAttribute("style", STALL_STYLE_ACTIVE);
      }
    };

    const companiesContainer = document.getElementById('companiesContainer');

    const showActiveCompany = (stallNumber_: number | null) => {
      if (!companiesContainer) {
        return;
      }

      while (companiesContainer.firstChild) {
        companiesContainer.removeChild(companiesContainer.firstChild);
      }
      //console.log("highlightStallMobile: " + stallNumber_);
      stallMap.forEach((company) => {
        if (company.stallNumber === stallNumber_) {
          showCompany(company, 'block');
        }
      });

    }

    const showCompanyList = () => {

      /*const element = document.getElementById('hint');
      if (element && toggledButtonsMajorA.size != 0) {
        element.style.display = 'none';
      }*/

      if (searchWasRecent && toggledButtonsMajorA.size !== 0) {
        setSearchTerm('');
        setSearchWasRecent(false);
      }

      // Clear existing content
      if (!companiesContainer) {
        return;
      }

      while (companiesContainer.firstChild) {
        companiesContainer.removeChild(companiesContainer.firstChild);
      }

      const filteredCompanies = sortCompaniesAlphabetically(getCompanies(toggledButtonsMajorA, toggledButtonsMajorB));

      filteredCompanies.forEach((company) => {
        const isActiveCompany = company.stallNumber !== null && company.stallNumber === active;
        showCompany(company, isActiveCompany ? 'block' : 'none');
      });

      /*if (toggledButtonsMajorA.size == 0 && toggledButtonsMajorB.size == 5) {
        showAllCompanies();
      }*/

      const empty = document.getElementById('empty_search');
      if (toggledButtonsMajorA.size !== 0 && toggledButtonsMajorB.size !== 0) {
        if (empty) {
          empty.style.display = 'none';
        }
      } else {
        if (empty) {
          empty.style.display = 'block';
        }
      }
    };

    const showCompanyListBySearch = () => {
      if (!companiesContainer) {
        return;
      }

      while (companiesContainer.firstChild) {
        companiesContainer.removeChild(companiesContainer.firstChild);
      }

      const normalizedSearchTerm = normalizeSearchValue(searchTerm);

      const searchFilter = (company: CompanyJobfairInfo) => {
        return normalizeSearchValue(company.companyName).includes(normalizedSearchTerm);
      };

      var filtered = sortCompaniesAlphabetically(Array.from(stallMap.values()).filter(searchFilter));

      if (toggledButtonsMajorA.size === 0) {
        setSearchWasRecent(true);
      }

      filtered.forEach((company) => {
        const isActiveCompany = company.stallNumber !== null && company.stallNumber === active;
        showCompany(company, isActiveCompany ? 'block' : 'none');
      });

    };

    const showCompany = (company: CompanyJobfairInfo, display: string) => {
      const ikarieraLink = ikarieraLinks.get(normalizeCompanyKey(company.companyName));
      // Create the company row (visible part) including the stall number
      const companyRow = document.createElement('div');
      const formattedStallNumber = company.stallNumber === null
        ? ''
        : String(company.stallNumber).padEnd(2, ' ');

      companyRow.classList.add('company-row'); // Use for styling
      companyRow.style.cursor = 'pointer';
      companyRow.style.fontWeight = 'bold';

      const companyNameLabel = document.createElement('span');
      companyNameLabel.classList.add('company-row-name');
      companyNameLabel.textContent = company.companyName;

      const stallNumberLabel = document.createElement('span');
      stallNumberLabel.classList.add('company-row-stall');

      if (!isIndirectParticipation(company.stallNumber)) {
        stallNumberLabel.textContent = '';

        const stallPrefix = document.createElement('span');
        stallPrefix.classList.add('company-row-stall-prefix');
        stallPrefix.textContent = 'Stánek ';

        const stallNumberValue = document.createElement('span');
        stallNumberValue.classList.add('company-row-stall-number');
        stallNumberValue.textContent = formattedStallNumber;

        stallNumberLabel.appendChild(stallPrefix);
        stallNumberLabel.appendChild(stallNumberValue);
      } else {
        stallNumberLabel.textContent = 'Nepřímá účast';
      }

      companyRow.appendChild(companyNameLabel);
      companyRow.appendChild(stallNumberLabel);

      // Info container (hidden part) for additional details
      const infoContainer = document.createElement('div');
      infoContainer.classList.add('info-container'); // Use for styling

      // Company description
      const description = document.createElement('p');
      description.textContent = company.description;
      infoContainer.appendChild(description);

      // Company link (if available)
      if (company.www) {
        const link = document.createElement('a');
        link.href = company.www;
        link.textContent = 'Zjistit více';
        link.target = '_blank'; // Open in a new tab
        link.rel = 'noreferrer';
        infoContainer.appendChild(link);
        link.style.backgroundColor = '#1b75bb'; // Background color
      }

      if (ikarieraLink) {
        const offerLink = document.createElement('a');
        offerLink.href = ikarieraLink;
        offerLink.textContent = 'Nab\u00eddky';
        offerLink.target = '_blank';
        offerLink.rel = 'noreferrer';
        offerLink.style.backgroundColor = '#1b75bb';
        infoContainer.appendChild(offerLink);
      }

      const stall = document.createElement('a');
      stall.id = 'reference-to-stall';
      stall.textContent = 'Ukaž na plánku';
      stall.style.backgroundColor = '#1b75bb'; // Background color
      stall.style.color = '#ffffff';
      stall.addEventListener('click', (event) => {
        window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top smoothly
      });

      infoContainer.appendChild(stall);

      // Initially hide the info container
      infoContainer.style.display = display;

      // Toggle the visibility of the info container on click
      companyRow.addEventListener('click', () => {
        userStartedBrowsing.current = true;
        const shouldOpen = infoContainer.style.display === 'none';

        companiesContainer
          ?.querySelectorAll('.info-container')
          .forEach((container) => ((container as HTMLElement).style.display = 'none'));

        if (shouldOpen) {
          highlightStallMobile(company.stallNumber);
          setActive(company.stallNumber ?? 0);
          infoContainer.style.display = 'block';
        } else {
          highlightStallMobile(null);
          setActive(0);
        }
      });

      // Append elements to the companies container
      companiesContainer?.appendChild(companyRow);
      companiesContainer?.appendChild(infoContainer);
    }

    const showCompanyFromUrl = () => {
      if (handledUrlSelection.current || userStartedBrowsing.current) {
        return;
      }

      const params = new URLSearchParams(location.search);
      const numberParam = params.get('number');

      if (!numberParam) {
        return;
      }

      const stall_num = parseInt(numberParam, 10);

      if (Number.isNaN(stall_num)) {
        return;
      }

      console.log("Number from URL:", stall_num);

      handledUrlSelection.current = true;
      linkedStallNumber.current = stall_num;

      showActiveCompany(stall_num);
      highlightStallMobile(stall_num);
      setActive(stall_num);

      window.history.replaceState(null, "", "/plan");

      const empty = document.getElementById('empty_search');
      if (empty) {
        empty.style.display = 'none';
      }

      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        window.scrollTo({ top: 600, behavior: "smooth" });
      }
    };

    // Function to be called after the SVG is loaded
    const onSvgLoad = () => {
      highlightStalls();
      showCompanyList();

      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        window.scrollTo({ top: 200, behavior: "smooth" });
      }

      showCompanyFromUrl();
    };

    // Add event listener on load to highlight stalls
    //jobfairPlan?.addEventListener("load", highlightStalls, false);
    companiesContainer?.addEventListener("load", showCompanyList, false);
    jobfairPlan?.addEventListener("load", onSvgLoad, false);

    if (jobfairPlan && jobfairPlan.contentDocument) {
      highlightStalls();
      if (linkedStallNumber.current !== null && !userStartedBrowsing.current) {
        showActiveCompany(linkedStallNumber.current);
        highlightStallMobile(linkedStallNumber.current);
      } else if (searchTerm !== '') {
        linkedStallNumber.current = null;
        showCompanyListBySearch();
      } else if (toggledButtonsMajorA.size !== 0) {
        linkedStallNumber.current = null;
        showCompanyList();
      }
      else {
        showCompanyList();
      }

      showCompanyFromUrl();
    }

    return () => {
      jobfairPlan?.removeEventListener("load", highlightStalls, false);
      companiesContainer?.removeEventListener("load", showCompanyList, false);
      jobfairPlan?.removeEventListener("load", onSvgLoad, false);
    };
  }, [active, ikarieraLinks, location.search, navigate, planId, searchTerm, searchWasRecent, toggledButtonsMajorA, toggledButtonsMajorB]);


  return (


    <div className="container-column pt-24">
      {/* Mobile device layout ------------------------ */}
      <div className="mobile-version">
        <div className='mobile-planek1'>
          <object
            className="block mx-auto w-[95%]"
            data={props.svgPathMobile}
            type="image/svg+xml"
            id="jobfair-plan-mobile"
          >
            Plánek Veletrhu pracovních příležitostí iKariéra 2025
          </object></div>
        <div className='mobile-buttonform'>
          <ButtonForm
            toggledButtonsMajorA={toggledButtonsMajorA}
            setToggledButtonsMajorA={setToggledButtonsMajorAWithBrowseTracking}
            toggledButtonsMajorB={toggledButtonsMajorB}
            setToggledButtonsMajorB={setToggledButtonsMajorBWithBrowseTracking}
            enableAllButtonMajorA={true}
            enableAllButtonMajorB={true}
          />
        </div>

        {/* <button id="toggleSvgButton"  onClick={() => handlePlanToggle()} >Ukázat na plánku</button> */}

        <h5 id='empty_search' className='text-center mx-4 text-[#AA0000] font-bold' style={{ display: 'block' }}>Vyber si obor a typ úvazku, nebo vyhledej firmu podle názvu!</h5>

        <div className='search-mobile'>
          <input className='input-mobile'
            type="text"
            placeholder="Vyledej podle názvu"
            value={searchTerm}
            onChange={(e) => {
              userStartedBrowsing.current = true;
              linkedStallNumber.current = null;
              setActive(0);
              setSearchTerm(e.target.value);
            }
            }
          />
        </div>
        <h4 id='hint' className='centered-hint'>⇓ Seznam firem ⇓</h4>
        <div id="companiesContainer">
        </div>
      </div>
      {/* Web browser layout ------------------------ */}
      <div className="container-row">
        <div className="buttonform1">
          <ButtonForm
            toggledButtonsMajorA={toggledButtonsMajorA}
            setToggledButtonsMajorA={setToggledButtonsMajorAWithBrowseTracking}
            toggledButtonsMajorB={toggledButtonsMajorB}
            setToggledButtonsMajorB={setToggledButtonsMajorBWithBrowseTracking}
            enableAllButtonMajorA={false}
            enableAllButtonMajorB={false}
          />
        </div>
        <div className="h-screen absolute pt-0 flex justify-center">
          <object className='svg_img ml-20 mr-24 mt-8 z-0' data={props.svgPath} type="image/svg+xml" id="jobfair-plan">
            Plánek Veletrhu pracovních příležitostí iKariéra 2025
          </object>
        </div>
        <div className="w-full h-screen flex justify-between">
          <div className='w-1/5 h-1/2 z-10 choose-options'>
            <div className="w-1/2 h-full container-column card-background z-50">
              <div className='buttonform'>
                <ButtonForm
                  toggledButtonsMajorA={toggledButtonsMajorA}
                  setToggledButtonsMajorA={setToggledButtonsMajorAWithBrowseTracking}
                  toggledButtonsMajorB={toggledButtonsMajorB}
                  setToggledButtonsMajorB={setToggledButtonsMajorBWithBrowseTracking}
                  enableAllButtonMajorA={false}
                  enableAllButtonMajorB={false}
                />
              </div>
            </div>
          </div>
          <div className='w-1/2 h-1/2 z-10 card-company'>
            <div className=" w-1/2 h-full container-column card-background z-50">
              <div className="card-subtitle">
                <h3>{active ? (isIndirectParticipation(active) ? "Nepřímá účast" : "Stánek č. " + active) : "Vyber firmu kliknutím na stánek v plánku!"}</h3>
              </div>
              <div className="card-title"><a href={stallMap.get(active)?.www} target='_blank' rel="noreferrer" style={{ textDecoration: 'none' }}><h1>{stallMap.get(active)?.companyName}</h1></a></div>
              <div className="card-description">{stallMap.get(active)?.description}</div>
            </div>
          </div>

        </div>
        <div id="companiesContainer">
        </div>
      </div>
    </div>
  );
}
<div id="companyList"></div>

export default InteractivePlan;
